import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema, { getArticleSchema } from '@/components/feature/SEOSchema';
import { articles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const category = article
    ? categories.find((c) => c.slug === article.category)
    : null;

  const relatedArticles = article
    ? articles
        .filter((a) => a.category === article.category && a.slug !== article.slug)
        .slice(0, 3)
    : [];

  const schemaData = article
    ? {
        title: article.title,
        description: article.excerpt,
        url: `https://999vv.cn/article/${article.slug}`,
        datePublished: article.date,
        dateModified: article.date,
        imageUrl: article.coverImage,
        authorName: '偷拍自拍技巧大全',
      }
    : null;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">
              文章未找到
            </h1>
            <p className="text-foreground-500 mb-6">你访问的文章可能已被移除或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOSchema type="article" data={schemaData as unknown as Record<string, unknown>} />
      {schemaData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              getArticleSchema(schemaData)
            ),
          }}
        />
      )}
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              category
                ? { label: category.name, href: `/category/${category.slug}` }
                : { label: '分类' },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <header className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 flex-wrap text-sm text-foreground-500 mb-6">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </div>
                <span>{article.date}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </div>
                <span>{article.readTime} 分钟阅读</span>
              </div>
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="flex items-center gap-1.5 text-primary-500 hover:text-primary-600 transition-colors"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={category.icon}></i>
                  </div>
                  <span>{category.name}</span>
                </Link>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          {/* Cover Image */}
          <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
              title={`${article.title} - 偷拍自拍技巧大全`}
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-lg max-w-none mb-12
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-5
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
              prose-strong:text-foreground-900 prose-strong:font-semibold
              prose-li:text-foreground-700
              prose-a:text-primary-500 hover:prose-a:text-primary-600"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Divider */}
          <div className="border-t border-background-200/60 pt-8 mb-12">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <Link
                to={category ? `/category/${category.slug}` : '/'}
                className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </div>
                返回{category ? category.name : '首页'}
              </Link>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-400">分享到：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors">
                  <i className="ri-wechat-line text-foreground-600 text-sm"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors">
                  <i className="ri-weibo-line text-foreground-600 text-sm"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors">
                  <i className="ri-link text-foreground-600 text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {rel.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {rel.excerpt}
                      </p>
                      <div className="mt-3 text-xs text-foreground-400">{rel.date}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}